import { useEffect, useState } from "react";
import { GraduationCap, Plus, Trash2 } from "lucide-react";
import { API, errMsg, TOPIC_NAMES, fmtDate } from "../lib/api";
import { PageHeader, EmptyState, DifficultyBadge } from "../components/bits";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";

const RATINGS = [
  { key: "hard", label: "Hard", hint: "Repeat soon", cls: "border-destructive text-destructive hover:bg-destructive/10", testid: "rate-hard-btn" },
  { key: "medium", label: "Good", hint: "Normal interval", cls: "border-primary text-primary hover:bg-secondary", testid: "rate-medium-btn" },
  { key: "easy", label: "Easy", hint: "Longer interval", cls: "border-success text-success hover:bg-success/10", testid: "rate-easy-btn" },
];

function StudyTab({ onProgress }) {
  const [queue, setQueue] = useState(null);
  const [idx, setIdx] = useState(0);
  const [flipped, setFlipped] = useState(false);

  const load = async () => {
    try {
      const { data } = await API.get("/flashcards/due");
      setQueue(data);
      setIdx(0);
      setFlipped(false);
    } catch (e) { toast.error(errMsg(e)); }
  };

  useEffect(() => { load(); }, []);

  const rate = async (rating) => {
    const card = queue[idx];
    try {
      await API.post(`/flashcards/${card.id}/review`, { rating });
    } catch (e) { toast.error(errMsg(e)); return; }
    const q = [...queue];
    if (rating === "hard") q.push(card); // hard cards repeat until rated Good/Easy
    setQueue(q);
    setFlipped(false);
    setIdx(idx + 1);
    onProgress();
  };

  if (queue === null) return <p className="py-16 text-center text-sm text-muted-foreground">Loading your cards…</p>;
  if (queue.length === 0 || idx >= queue.length) {
    return (
      <EmptyState title={queue.length === 0 ? "No cards due" : "Session complete"}
        body={queue.length === 0
          ? "Create your own flashcards in the Create tab to start studying."
          : "You've reviewed every due card. Hard-rated cards will come back soon — easy ones will wait longer."}
        testid="study-complete">
        <p className="text-xs text-muted-foreground">{queue.length > 0 ? `${queue.length} cards reviewed this session` : ""}</p>
      </EmptyState>
    );
  }

  const card = queue[idx];
  return (
    <div className="max-w-2xl mx-auto" data-testid="study-area">
      <p className="text-xs tracking-[0.15em] uppercase font-semibold text-muted-foreground mb-4 text-center" data-testid="study-progress">
        Card {idx + 1} of {queue.length}{card.state !== "new" ? ` · ${card.state}` : " · new"}
      </p>
      <button onClick={() => setFlipped(!flipped)} data-testid="flashcard-card"
        className="w-full bg-card border border-border rounded-lg shadow-sm min-h-[280px] p-8 md:p-10 text-left transition-all duration-200 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-primary">
        {!flipped ? (
          <div>
            <p className="text-xs tracking-[0.15em] uppercase font-semibold text-muted-foreground mb-4">
              Question{card.topic ? ` · ${TOPIC_NAMES[card.topic] || card.topic}` : ""}
            </p>
            <p className="text-base md:text-lg font-medium leading-relaxed whitespace-pre-wrap" data-testid="flashcard-front">{card.front}</p>
          </div>
        ) : (
          <div className="animate-fade-up">
            <p className="text-xs tracking-[0.15em] uppercase font-semibold text-success mb-4">Answer</p>
            <p className="text-sm md:text-base leading-relaxed whitespace-pre-wrap" data-testid="flashcard-back">{card.back}</p>
          </div>
        )}
      </button>
      {!flipped ? (
        <div className="mt-5 flex justify-center">
          <Button data-testid="flip-card-btn" onClick={() => setFlipped(true)} className="px-10">Show answer</Button>
        </div>
      ) : (
        <div className="mt-5" data-testid="rating-buttons">
          <p className="text-xs text-muted-foreground text-center mb-3">How well did you know it?</p>
          <div className="grid grid-cols-3 gap-3">
            {RATINGS.map((r) => (
              <button key={r.key} data-testid={r.testid} onClick={() => rate(r.key)}
                className={`border rounded-lg px-4 py-3 text-sm font-semibold transition-colors ${r.cls}`}>
                {r.label}
                <span className="block text-xs font-normal mt-0.5 opacity-70">{r.hint}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function CreateTab({ onCreated }) {
  const [front, setFront] = useState("");
  const [back, setBack] = useState("");
  const [topic, setTopic] = useState("none");
  const [saving, setSaving] = useState(false);

  const createManual = async () => {
    setSaving(true);
    try {
      await API.post("/flashcards", { front, back, topic: topic === "none" ? null : topic });
      toast.success("Flashcard created");
      setFront(""); setBack("");
      onCreated();
    } catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };

  return (
    <div className="max-w-xl" data-testid="create-tab">
      <div className="bg-card border border-border rounded-lg p-6">
        <h3 className="font-display font-semibold mb-4 flex items-center gap-2"><Plus className="w-4 h-4" /> Create manually</h3>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="fc-front">Front (question)</Label>
            <Textarea id="fc-front" data-testid="manual-front-input" rows={4} value={front}
              onChange={(e) => setFront(e.target.value)} placeholder="e.g. What is the first-line investigation for suspected heart failure?" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="fc-back">Back (answer)</Label>
            <Textarea id="fc-back" data-testid="manual-back-input" rows={4} value={back}
              onChange={(e) => setBack(e.target.value)} placeholder="e.g. Serum natriuretic peptide (BNP/NT-proBNP) — NICE NG106" />
          </div>
          <div className="space-y-2">
            <Label>Topic (optional)</Label>
            <Select value={topic} onValueChange={setTopic}>
              <SelectTrigger data-testid="manual-topic-select"><SelectValue placeholder="None" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">None</SelectItem>
                {Object.entries(TOPIC_NAMES).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <Button data-testid="create-manual-btn" onClick={createManual} disabled={saving || front.trim().length < 3 || !back.trim()}>
            {saving ? "Creating…" : "Create flashcard"}
          </Button>
        </div>
      </div>
    </div>
  );
}

function MyCardsTab({ cards, onChanged }) {
  const del = async (c) => {
    try {
      await API.delete(`/flashcards/${c.id}`);
      toast.success("Flashcard deleted");
      onChanged();
    } catch (e) { toast.error(errMsg(e)); }
  };

  if (!cards?.length) {
    return <EmptyState title="No flashcards yet" body="Create your own cards in the Create tab." testid="cards-empty" />;
  }
  return (
    <div className="space-y-3" data-testid="cards-list">
      {cards.map((c) => (
        <div key={c.id} className="bg-card border border-border rounded-lg p-4 flex items-start gap-4" data-testid={`card-row-${c.id}`}>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium line-clamp-2">{c.front}</p>
            <p className="text-xs text-muted-foreground mt-1.5">
              {c.source === "question" ? "From question bank" : "Manual"}
              {c.topic ? ` · ${TOPIC_NAMES[c.topic] || c.topic}` : ""}
              {" · "}{c.state}{c.interval_days ? ` · every ${c.interval_days}d` : ""}
              {c.reps ? ` · ${c.reps} reviews` : ""}
              {c.lapses ? ` · ${c.lapses} lapses` : ""}
            </p>
          </div>
          <span className={`text-xs font-semibold px-2 py-0.5 rounded-full shrink-0 ${
            new Date(c.due) <= new Date() ? "bg-destructive/10 text-destructive" : "bg-muted text-muted-foreground"}`}>
            {new Date(c.due) <= new Date() ? "due now" : `due ${fmtDate(c.due)}`}
          </span>
          <button data-testid={`delete-card-${c.id}`} onClick={() => del(c)}
            className="p-2 text-muted-foreground hover:text-destructive transition-colors shrink-0" aria-label="Delete flashcard">
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      ))}
    </div>
  );
}

export default function Flashcards() {
  const [tab, setTab] = useState("study");
  const [stats, setStats] = useState(null);

  const refresh = async () => {
    try {
      const { data } = await API.get("/flashcards");
      setStats(data);
    } catch (e) { toast.error(errMsg(e)); }
  };
  useEffect(() => { refresh(); }, []);

  const TABS = [
    { key: "study", label: "Study", testid: "study-tab" },
    { key: "create", label: "Create", testid: "create-tab-btn" },
    { key: "cards", label: "My cards", testid: "cards-tab-btn" },
  ];

  return (
    <div data-testid="flashcards-page" className="max-w-5xl">
      <PageHeader title="Flashcards" sub="Spaced repetition, Anki-style: rate each card Hard, Good or Easy — hard cards keep coming back until you know them." />
      {stats && (
        <div className="flex flex-wrap gap-6 mb-6 text-sm" data-testid="flashcard-stats">
          <span><span className="font-display font-bold text-xl" data-testid="stat-total-cards">{stats.total}</span> <span className="text-muted-foreground">cards</span></span>
          <span><span className="font-display font-bold text-xl text-destructive" data-testid="stat-due-cards">{stats.due}</span> <span className="text-muted-foreground">due now</span></span>
          <span><span className="font-display font-bold text-xl text-primary" data-testid="stat-new-cards">{stats.new}</span> <span className="text-muted-foreground">new</span></span>
        </div>
      )}
      <div className="flex gap-2.5 mb-6">
        {TABS.map((t) => (
          <button key={t.key} data-testid={t.testid} onClick={() => setTab(t.key)}
            className={`px-4 py-2 rounded-md border text-sm font-semibold transition-colors ${
              tab === t.key ? "bg-primary text-primary-foreground border-primary" : "border-border bg-card hover:border-primary/50"}`}>
            {t.label}
          </button>
        ))}
      </div>
      {tab === "study" && <StudyTab onProgress={refresh} />}
      {tab === "create" && <CreateTab onCreated={refresh} />}
      {tab === "cards" && <MyCardsTab cards={stats?.cards} onChanged={refresh} />}
    </div>
  );
}
